import React from 'react'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import { getHeadshot } from './../../../../shared/utilities/getHeadshot.function'
import {
  getRaceKeyFromMilestone,
  getRaceThresholdForMilestone,
} from './../../../../constants/race-to-the-ring.constants'
import Store from './../../store'
import env from './../../../../environments/environment'

import './RecentRaceWinners.component.scss'

const RECENT_LIMIT = 3

const getRingImageUrl = filename => {
  const storageBucket = env.integrations.firebase.config.storageBucket
  const encodedPath = encodeURIComponent(`static/rings/${filename}`)
  return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/${encodedPath}?alt=media`
}

const AgentIcon = () => (
  <svg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg' role='img' aria-label='Agent'>
    <circle cx='24' cy='16' r='10' fill='none' stroke='currentColor' strokeWidth='3' />
    <path
      d='M8 44c0-8.84 7.16-16 16-16s16 7.16 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinecap='round'
    />
    <path d='M24 25v12' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    <path
      d='M18 37l6-4 6 4'
      fill='none'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const LeaderIcon = () => (
  <svg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg' role='img' aria-label='Leader'>
    <circle cx='16' cy='16' r='8' fill='none' stroke='currentColor' strokeWidth='3' />
    <path d='M4 44c0-7.18 5.82-13 13-13h2' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    <rect x='26' y='10' width='16' height='10' rx='3' fill='none' stroke='currentColor' strokeWidth='3' />
    <path d='M34 20v14' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    <path d='M28 38h12' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    <circle cx='34' cy='32' r='3' fill='currentColor' />
  </svg>
)

const ExecutiveIcon = () => (
  <svg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg' role='img' aria-label='CEO'>
    <path
      d='M8 18l7-10 9 5 9-5 7 10-6 22H14L8 18z'
      fill='none'
      stroke='currentColor'
      strokeWidth='3'
      strokeLinejoin='round'
    />
    <circle cx='24' cy='19' r='4' fill='currentColor' />
    <path d='M16 40h16' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
    <path d='M12 27h24' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
  </svg>
)

const ROLE_ICONS = {
  agent: AgentIcon,
  leader: LeaderIcon,
  executive: ExecutiveIcon,
}

const getNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatPoints = value => `${getNumber(value).toLocaleString()} pts`

const formatRaceLabel = raceKey => {
  if (!raceKey) return 'Agent'
  return raceKey
    .split(/[\s-_]/)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const getMilestoneValue = (milestone, key) => {
  if (!milestone) return undefined
  if (typeof milestone.get === 'function') {
    const value = milestone.get(key)
    if (typeof value !== 'undefined') return value
  }
  if (typeof milestone.raw === 'function') {
    const raw = milestone.raw()
    if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key]
  }
  if (typeof milestone === 'object' && milestone !== null) {
    return milestone[key]
  }
  return undefined
}

const getWinnerName = milestone => {
  const first = getMilestoneValue(milestone, 'mi_fname') ?? ''
  const last = getMilestoneValue(milestone, 'mi_lname') ?? ''
  const name = `${first} ${last}`.trim()
  return name || 'TBD'
}

const getWinnerHeadshot = milestone => {
  const userId = getMilestoneValue(milestone, 'user_id')
  const user = Store.getUserById(userId)
  const userPicture =
    (typeof user?.get === 'function' && user.get('u_picture')) ??
    (typeof user?.raw === 'function' ? user.raw()?.u_picture : undefined)
  const milestonePicture =
    getMilestoneValue(milestone, 'u_picture') ??
    getMilestoneValue(milestone, 'pic') ??
    getMilestoneValue(milestone, 'image')

  return getHeadshot({
    u_picture: userPicture ?? milestonePicture,
    id: userId,
  })
}

const getThresholdAtWin = (milestone, raceKey) => {
  const userId = getMilestoneValue(milestone, 'user_id')
  const recipient = Store.getRecipientByUserId(userId, raceKey)

  if (recipient?.get) {
    const threshold = recipient.get('ring_threshold')
    if (typeof threshold !== 'undefined' && threshold !== null) {
      return getNumber(threshold)
    }
  }

  return getNumber(getRaceThresholdForMilestone(milestone))
}

const formatWinDate = milestone => {
  const dateValue = getMilestoneValue(milestone, 'mi_date')
  return dateValue ? moment(dateValue).format('MMM D, YYYY') : 'Date unavailable'
}

const RecentRaceWinners = () => {
  const winners = Array.isArray(Store?.Winners?.all) ? Store.Winners.all : []

  const recentWinners = winners
    .slice()
    .sort((a, b) => {
      const aDate = new Date(getMilestoneValue(a, 'mi_date') || 0).getTime()
      const bDate = new Date(getMilestoneValue(b, 'mi_date') || 0).getTime()
      return bDate - aDate
    })
    .slice(0, RECENT_LIMIT)

  if (Store.isLoading) {
    return (
      <div id='RecentRaceWinnersComponent'>
        <LoadingSpinner size='md' isActive={true} label='Loading Race to the Ring data' />
      </div>
    )
  }

  if (!recentWinners.length) {
    return (
      <section id='RecentRaceWinnersComponent' className='recent-winners'>
        <img src={getRingImageUrl('royal-ring-banner.png')} alt='' className='recent-winners__banner' />
        <h1 className='recent-winners__congratulations'>Congratulations to our Recent Inductees!</h1>
        <p className='recent-winners__empty'>We'll celebrate the latest champions as soon as results are available.</p>
      </section>
    )
  }

  return (
    <section id='RecentRaceWinnersComponent' className='recent-winners'>
      <img src={getRingImageUrl('royal-ring-banner.png')} alt='' className='recent-winners__banner' />
      <h1 className='recent-winners__congratulations'>Congratulations to our Recent Inductees!</h1>
      <div className='recent-winners__list'>
        {recentWinners.map(winner => {
          const raceKey = getRaceKeyFromMilestone(winner)
          const raceLabel = formatRaceLabel(raceKey)
          const RoleIcon = ROLE_ICONS[raceKey] || ROLE_ICONS.agent
          const threshold = getThresholdAtWin(winner, raceKey)
          const headshot = getWinnerHeadshot(winner)
          const winDate = formatWinDate(winner)

          return (
            <article key={winner?.get?.('milestone_id')} className='recent-winners__card'>
              <div className='recent-winners__hero'>
                <img
                  src={headshot}
                  alt={getWinnerName(winner)}
                  className='recent-winners__avatar recent-winners__avatar--hero'
                />
                <div className='recent-winners__hero-body'>
                  <div className='recent-winners__hero-header'>
                    <span className='recent-winners__badge'>{raceLabel} Winner</span>
                    <span className={`recent-winners__icon recent-winners__icon--${raceKey}`} aria-hidden='true'>
                      <RoleIcon />
                    </span>
                  </div>
                  <h3 className='recent-winners__name'>{getWinnerName(winner)}</h3>
                  <p className='recent-winners__date'>{winDate}</p>
                </div>
              </div>

              <div className='recent-winners__stats'>
                <div className='recent-winners__stat'>
                  <span className='recent-winners__stat-label'>Ring Threshold</span>
                  <strong className='recent-winners__stat-value'>{formatPoints(threshold)}</strong>
                </div>
              </div>

              <div className='recent-winners__club'>
                <p className='recent-winners__label'>Royal Ring Club</p>
                <p className='recent-winners__subtext'>Secured at {formatPoints(threshold)}</p>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default observer(RecentRaceWinners)
